import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Save, X, Settings2, Copy, Layers, Rocket, Clock, Globe, Check, Trash2 } from "lucide-react";
import { formatApiError } from "@/lib/api";

const input = "w-full bg-transparent border hairline px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--brand-primary)]";

// White-label parent domain for tenant subdomains (adjust when your prod domain changes).
const PARENT_DOMAIN = "learninghub.emmaus-partners.com";

function copyText(txt) {
  navigator.clipboard?.writeText(txt).then(
    () => toast.success("Copied to clipboard"),
    () => toast.error("Copy failed")
  );
}

function daysRemaining(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.ceil(ms / 86400000);
}

const BRAND_FIELDS = [
  { key: "logo_text", label: "Logo text (short)", type: "text" },
  { key: "tagline", label: "Tagline", type: "text" },
  { key: "primary_color", label: "Primary color (hex)", type: "text" },
  { key: "bg_color", label: "Background color (hex)", type: "text" },
  { key: "surface2_color", label: "Surface color (hex)", type: "text" },
  { key: "hero_eyebrow", label: "Hero eyebrow", type: "text" },
  { key: "hero_title", label: "Hero title", type: "text" },
  { key: "hero_image", label: "Hero image URL", type: "text" },
  { key: "testimonial_quote", label: "Testimonial quote", type: "text" },
  { key: "testimonial_author", label: "Testimonial author", type: "text" },
  { key: "from_email", label: "Email sender (from address)", type: "text" },
  { key: "signatory_name", label: "Certificate signatory name", type: "text" },
  { key: "signatory_title", label: "Certificate signatory title", type: "text" },
  { key: "custom_domain", label: "Custom domain (e.g. academy.acme.com)", type: "text" },
];

export default function WorkspacesTab({ activeSlug, onSwitch }) {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [trialing, setTrialing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [trialResult, setTrialResult] = useState(null);
  const [domainId, setDomainId] = useState(null);

  const load = () => api.get("/workspaces").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setWsStatus = async (w, status) => {
    const warnings = {
      inactive: `Inactivate ${w.name}? All its users will be blocked from signing in until reactivated.`,
      cancelled: `Cancel ${w.name}? All its users will be blocked from signing in. Data is kept for records.`,
    };
    if (warnings[status] && !window.confirm(warnings[status])) return;
    try {
      await api.post(`/workspaces/${w.id}/status`, { status });
      toast.success(`${w.name} is now ${status}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const deleteWs = async (w) => {
    if (!window.confirm(`PERMANENTLY delete ${w.name} (/${w.slug}) and ALL its users, content, documents and progress? This cannot be undone.`)) return;
    if (!window.confirm(`Last check — really delete /${w.slug} forever?`)) return;
    try {
      await api.delete(`/workspaces/${w.id}`);
      toast.success(`Workspace ${w.slug} deleted`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        <button onClick={() => { setCreating(true); setTrialing(false); setEditingId(null); }} className="btn-ghost text-sm flex items-center gap-2" data-testid="ws-new">
          <Plus size={14} /> New workspace
        </button>
        <button onClick={() => { setTrialing(true); setCreating(false); setEditingId(null); setTrialResult(null); }} className="btn-primary text-sm flex items-center gap-2" data-testid="ws-new-trial">
          <Rocket size={14} /> Start a 14-day trial
        </button>
      </div>

      {creating && (
        <CreateForm
          sources={items}
          onCancel={() => setCreating(false)}
          onSaved={() => { setCreating(false); load(); }}
        />
      )}

      {trialing && (
        <TrialForm
          onCancel={() => setTrialing(false)}
          onSaved={(res) => { setTrialing(false); setTrialResult(res); load(); }}
        />
      )}

      {trialResult && (
        <div className="border-2 border-[color:var(--brand-primary)] bg-white p-5" data-testid="trial-result">
          <div className="label-eyebrow text-[var(--brand-primary)] mb-2">Trial workspace ready 🎉</div>
          <div className="font-display text-xl">{trialResult.workspace.name} <span className="font-mono text-sm text-neutral-400">/{trialResult.workspace.slug}</span></div>
          <div className="mt-3 text-sm space-y-1">
            <div>Admin login: <span className="font-mono">{trialResult.admin_email}</span></div>
            <div>Expires: <span className="font-mono">{(trialResult.expires_at || "").slice(0, 10)}</span> (14 days)</div>
            <div>Shareable link: <a className="text-[var(--brand-primary)] link-underline" href={`/?w=${trialResult.workspace.slug}`} target="_blank" rel="noreferrer" data-testid="trial-link">{window.location.origin}/?w={trialResult.workspace.slug}</a></div>
          </div>
          <button className="btn-ghost text-xs mt-4" onClick={() => setTrialResult(null)}>Dismiss</button>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {items.map(w => {
          const rem = w.trial ? daysRemaining(w.trial_expires_at) : null;
          const expired = w.trial && rem != null && rem <= 0;
          return (
          <div key={w.id} className="border hairline bg-white p-5" data-testid={`ws-card-${w.slug}`}>
            {editingId === w.id ? (
              <BrandForm ws={w} onCancel={() => setEditingId(null)} onSaved={() => { setEditingId(null); load(); }} />
            ) : (
              <>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center text-xs font-display font-semibold text-white"
                         style={{ background: w.brand?.primary_color || "#0B3D2E" }}>
                      {w.brand?.logo_text || w.name?.slice(0, 3)}
                    </div>
                    <div>
                      <div className="font-display text-lg leading-tight">{w.name}</div>
                      <div className="font-mono text-xs text-neutral-400">/{w.slug}{activeSlug === w.slug ? " · active" : ""}</div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {w.trial && (
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 ${expired ? "bg-[#8B2E2E] text-white" : "border border-[color:var(--brand-primary)] text-[var(--brand-primary)]"}`} data-testid={`ws-trial-badge-${w.slug}`}>
                        <Clock size={10} /> {expired ? "Trial expired" : `Trial · ${rem}d left`}
                      </span>
                    )}
                    {w.status && w.status !== "active" && (
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 ${w.status === "cancelled" ? "bg-[#8B2E2E] text-white" : "border border-[#a05a00] text-[#a05a00]"}`} data-testid={`ws-status-badge-${w.slug}`}>
                        {w.status}
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-4 flex gap-4 text-xs text-neutral-500 font-mono">
                  <span>{w.counts?.modules ?? 0} mods</span>
                  <span>{w.counts?.resources ?? 0} res</span>
                  <span>{w.counts?.scenarios ?? 0} scn</span>
                  <span>{w.counts?.users ?? 0} users</span>
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  <button className="btn-primary text-xs flex items-center gap-1" onClick={() => onSwitch(w.slug)} data-testid={`ws-manage-${w.slug}`}>
                    <Layers size={12} /> Manage content
                  </button>
                  <button className="btn-ghost text-xs flex items-center gap-1" onClick={() => setEditingId(w.id)} data-testid={`ws-edit-${w.slug}`}>
                    <Settings2 size={12} /> Branding
                  </button>
                  <button className="btn-ghost text-xs flex items-center gap-1" onClick={() => setDomainId(domainId === w.id ? null : w.id)} data-testid={`ws-domain-${w.slug}`}>
                    <Globe size={12} /> Connect domain
                  </button>
                </div>
                {w.slug !== "emmaus" && (
                  <div className="mt-3 pt-3 border-t hairline flex flex-wrap gap-1 items-center">
                    <span className="label-eyebrow mr-1" style={{ fontSize: "0.5rem" }}>Status</span>
                    {(w.status && w.status !== "active") ? (
                      <button className="btn-ghost text-xs px-2 py-1" onClick={() => setWsStatus(w, "active")} data-testid={`ws-reactivate-${w.slug}`}>Reactivate</button>
                    ) : (
                      <>
                        <button className="btn-ghost text-xs px-2 py-1" onClick={() => setWsStatus(w, "inactive")} data-testid={`ws-inactivate-${w.slug}`}>Inactivate</button>
                        <button className="btn-ghost text-xs px-2 py-1" onClick={() => setWsStatus(w, "cancelled")} data-testid={`ws-cancel-${w.slug}`}>Cancel</button>
                      </>
                    )}
                    <button className="text-xs px-2 py-1 text-[#8B2E2E] hover:bg-[#8B2E2E]/10 transition-colors flex items-center gap-1" onClick={() => deleteWs(w)} data-testid={`ws-delete-${w.slug}`}>
                      <Trash2 size={11} /> Delete
                    </button>
                  </div>
                )}
                {domainId === w.id && <DomainPanel ws={w} onSaved={load} />}
              </>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}

function DomainPanel({ ws, onSaved }) {
  const slug = ws.slug;
  const [custom, setCustom] = useState(ws.brand?.custom_domain || "");
  const [busy, setBusy] = useState(false);
  const savedDomain = ws.brand?.custom_domain || "";
  const subdomain = `${slug}.${PARENT_DOMAIN}`;
  const primaryHost = savedDomain || subdomain;
  const fallback = `${window.location.origin}/?w=${slug}`;

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/workspaces/${ws.id}`, { brand: { ...(ws.brand || {}), custom_domain: custom.trim() } });
      toast.success(custom.trim() ? "Custom domain saved" : "Custom domain cleared");
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const Row = ({ label, value, testid }) => (
    <div className="flex items-center gap-2">
      <div className="flex-1 min-w-0">
        <div className="label-eyebrow" style={{ fontSize: "0.5rem" }}>{label}</div>
        <div className="font-mono text-xs truncate">{value}</div>
      </div>
      <button className="btn-ghost text-xs flex items-center gap-1 shrink-0" onClick={() => copyText(value)} data-testid={testid}>
        <Copy size={11} /> Copy
      </button>
    </div>
  );

  return (
    <div className="mt-4 border-t hairline pt-4 space-y-3 bg-[color:var(--brand-surface2)]/30 -mx-5 px-5 pb-1" data-testid={`domain-panel-${slug}`}>
      <div className="label-eyebrow text-[var(--brand-primary)]">Connect a custom domain</div>

      <div>
        <div className="label-eyebrow" style={{ fontSize: "0.5rem" }}>Bring your own domain (optional)</div>
        <div className="flex items-center gap-2 mt-1">
          <input
            className={input}
            placeholder={`e.g. academy.yourbrand.com — leave blank to use ${subdomain}`}
            value={custom}
            onChange={e => setCustom(e.target.value)}
            data-testid={`domain-custom-input-${slug}`}
          />
          <button disabled={busy} className="btn-primary text-xs flex items-center gap-1 shrink-0" onClick={save} data-testid={`domain-custom-save-${slug}`}>
            <Save size={12} /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <Row
        label={`1. Domain to link in Emergent (Deploy → Link domain → Entri)${savedDomain ? " — your custom domain" : " — default subdomain"}`}
        value={primaryHost}
        testid={`domain-copy-sub-${slug}`}
      />
      <div className="text-xs text-neutral-600 leading-relaxed">
        2. In your DNS provider (e.g. GoDaddy), add the <span className="font-mono">CNAME</span> record shown in the Entri flow for
        <span className="font-mono"> {primaryHost}</span>, and remove any conflicting <span className="font-mono">A</span> records for it.
        Emergent auto-provisions SSL. Propagation takes ~5–15 min.
      </div>
      <Row label="No-DNS fallback link (works immediately)" value={fallback} testid={`domain-copy-fallback-${slug}`} />
      <div className="flex items-center gap-1 text-xs text-neutral-500"><Check size={11} className="text-[var(--brand-primary)]" /> Once linked, the app loads this workspace fully branded at that address.</div>
    </div>
  );
}

function TrialForm({ onCancel, onSaved }) {
  const [f, setF] = useState({ business_name: "", slug: "", admin_name: "", admin_email: "", admin_password: "", primary_color: "#0B3D2E", industry: "", team_size: "" });
  const [busy, setBusy] = useState(false);
  const upd = (p) => setF({ ...f, ...p });

  const submit = async () => {
    if (!f.business_name || !f.slug || !f.admin_name || !f.admin_email || !f.admin_password) {
      toast.error("Please fill all required fields"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/workspaces/trial", { ...f, slug: f.slug.trim().toLowerCase() });
      toast.success("Trial workspace created");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="border hairline bg-white p-5 space-y-3" data-testid="trial-form">
      <div className="label-eyebrow text-[var(--brand-primary)]">New 14-day trial workspace</div>
      <div className="grid md:grid-cols-2 gap-3">
        <input className={input} placeholder="Business name *" value={f.business_name} onChange={e => upd({ business_name: e.target.value })} data-testid="trial-business" />
        <input className={input} placeholder="Subdomain / slug *" value={f.slug} onChange={e => upd({ slug: e.target.value })} data-testid="trial-slug" />
        <input className={input} placeholder="Admin full name *" value={f.admin_name} onChange={e => upd({ admin_name: e.target.value })} data-testid="trial-name" />
        <input className={input} placeholder="Work email *" value={f.admin_email} onChange={e => upd({ admin_email: e.target.value })} data-testid="trial-email" />
        <input className={input} type="password" placeholder="Password (min 6) *" value={f.admin_password} onChange={e => upd({ admin_password: e.target.value })} data-testid="trial-password" />
        <input className={input} placeholder="Industry" value={f.industry} onChange={e => upd({ industry: e.target.value })} data-testid="trial-industry" />
        <input className={input} placeholder="Team size (e.g. 1-10)" value={f.team_size} onChange={e => upd({ team_size: e.target.value })} data-testid="trial-teamsize" />
        <div className="flex items-center gap-2">
          <span className="text-xs text-neutral-500">Brand color</span>
          <input type="color" value={f.primary_color} onChange={e => upd({ primary_color: e.target.value })} className="h-9 w-16 border hairline bg-transparent" data-testid="trial-color" />
        </div>
      </div>
      <div className="text-xs text-neutral-500">Content is pre-filled by cloning the Emmaus curriculum. Trial expires in 14 days.</div>
      <div className="flex items-center gap-2 pt-1">
        <button disabled={busy} className="btn-primary flex items-center gap-2" onClick={submit} data-testid="trial-create-save"><Rocket size={14} /> {busy ? "Creating…" : "Create trial"}</button>
        <button className="btn-ghost flex items-center gap-2" onClick={onCancel}><X size={14} /> Cancel</button>
      </div>
    </div>
  );
}

function CreateForm({ sources, onCancel, onSaved }) {
  const [f, setF] = useState({ slug: "", name: "", clone_from: "", primary_color: "#0B3D2E" });
  const [busy, setBusy] = useState(false);
  const upd = (p) => setF({ ...f, ...p });

  const submit = async () => {
    if (!f.slug || !f.name) { toast.error("Slug and name are required"); return; }
    setBusy(true);
    try {
      await api.post("/workspaces", {
        slug: f.slug.trim().toLowerCase(),
        name: f.name.trim(),
        brand: { primary_color: f.primary_color, logo_text: f.name.slice(0, 3).toUpperCase() },
        clone_from: f.clone_from || null,
      });
      toast.success("Workspace created");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="border hairline bg-white p-5 space-y-3" data-testid="ws-create-form">
      <div className="grid md:grid-cols-3 gap-3">
        <input className={input} placeholder="Slug (e.g. proptix)" value={f.slug} onChange={e => upd({ slug: e.target.value })} data-testid="ws-slug" />
        <input className={input} placeholder="Name" value={f.name} onChange={e => upd({ name: e.target.value })} data-testid="ws-name" />
        <input className={input} placeholder="Primary color hex" value={f.primary_color} onChange={e => upd({ primary_color: e.target.value })} data-testid="ws-primary" />
      </div>
      <div className="flex items-center gap-2">
        <Copy size={14} className="text-neutral-400" />
        <select className={input} value={f.clone_from} onChange={e => upd({ clone_from: e.target.value })} data-testid="ws-clone">
          <option value="">Start empty (no clone)</option>
          {sources.map(s => <option key={s.slug} value={s.slug}>Clone content from: {s.name}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-2 pt-1">
        <button disabled={busy} className="btn-primary flex items-center gap-2" onClick={submit} data-testid="ws-create-save"><Save size={14} /> {busy ? "Creating…" : "Create"}</button>
        <button className="btn-ghost flex items-center gap-2" onClick={onCancel}><X size={14} /> Cancel</button>
      </div>
    </div>
  );
}

function BrandForm({ ws, onCancel, onSaved }) {
  const [name, setName] = useState(ws.name || "");
  const [brand, setBrand] = useState({ ...(ws.brand || {}) });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setBrand({ ...brand, [k]: v });

  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/workspaces/${ws.id}`, { name, brand });
      toast.success("Branding saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid={`ws-brand-form-${ws.slug}`}>
      <div className="label-eyebrow">Branding · /{ws.slug}</div>
      <input className={input} placeholder="Workspace name" value={name} onChange={e => setName(e.target.value)} data-testid="ws-brand-name" />
      <div className="grid md:grid-cols-2 gap-2">
        {BRAND_FIELDS.map(bf => (
          <input key={bf.key} className={input} placeholder={bf.label} value={brand[bf.key] || ""} onChange={e => upd(bf.key, e.target.value)} data-testid={`ws-brand-${bf.key}`} />
        ))}
      </div>
      <div className="flex items-center gap-2 pt-1">
        <button disabled={busy} className="btn-primary flex items-center gap-2" onClick={submit} data-testid="ws-brand-save"><Save size={14} /> {busy ? "Saving…" : "Save"}</button>
        <button className="btn-ghost flex items-center gap-2" onClick={onCancel}><X size={14} /> Cancel</button>
      </div>
    </div>
  );
}
